import { type Component, createSignal, Show } from 'solid-js';
import { DEFAULT_CONFIG } from '@duocast/shared';
import BottomSheet from './BottomSheet';
import PrePermissionNudge from './PrePermissionNudge';
import PermissionDenied, { type PermissionDenialReason } from './PermissionDenied';
import styles from './LandingPage.module.css';

export interface LandingPageProps {
  /** Number of users currently online */
  onlineCount: () => number;
  /** Called when user is ready to proceed (permissions granted). Receives the live MediaStream. */
  onStartChatting: (stream: MediaStream) => void;
}

/**
 * LandingPage — FE-002
 * The main entry screen for DuoCast. Handles:
 * - Hero with gradient wordmark + tagline + trust signals
 * - "Start Chatting" CTA with pulse animation
 * - Live online counter
 * - Peak-hour gating (off-peak shows "Notify Me")
 * - Pre-permission nudge overlay
 * - Permission denial UX (FE-004)
 * - Optional preferences bottom sheet
 */
const LandingPage: Component<LandingPageProps> = (props) => {
  const minOnline = DEFAULT_CONFIG.tuning.minOnlineForStart;

  // UI state
  const [isConnecting, setIsConnecting] = createSignal(false);
  const [showNudge, setShowNudge] = createSignal(false);
  const [showPermDenied, setShowPermDenied] = createSignal(false);
  const [permDeniedReason, setPermDeniedReason] = createSignal<PermissionDenialReason>('dismissed');
  const [showPrefs, setShowPrefs] = createSignal(false);

  // Preferences state (P2 — skippable)
  const [ageRange, setAgeRange] = createSignal('any');
  const [genderPref, setGenderPref] = createSignal('any');

  // Whether there are enough people online
  const isLive = () => props.onlineCount() >= minOnline;

  /** Step 1: User clicks "Start Chatting" — show pre-permission nudge */
  const handleStartClick = () => {
    setShowNudge(true);
  };

  /** Step 2: User clicks "Allow Access" in nudge — request permissions */
  const handleAllowAccess = async () => {
    setShowNudge(false);
    setIsConnecting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      // Keep the stream alive — pass it to the session manager
      // so we don't need to re-request permissions on the chat page.
      props.onStartChatting(stream);
    } catch (err: unknown) {
      setIsConnecting(false);

      if (err instanceof DOMException) {
        if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
          setPermDeniedReason('not-found');
        } else if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          // Distinguish "dismissed" vs "blocked"
          // When a user has previously blocked, the rejection is near-instant.
          // A dismissed prompt takes at least a small amount of time.
          // We check the permission state as a more reliable signal.
          try {
            const cameraStatus = await navigator.permissions.query({
              name: 'camera' as PermissionName,
            });
            setPermDeniedReason(cameraStatus.state === 'denied' ? 'blocked' : 'dismissed');
          } catch {
            // permissions.query not supported — default to dismissed
            setPermDeniedReason('dismissed');
          }
        } else {
          setPermDeniedReason('dismissed');
        }
      } else {
        setPermDeniedReason('dismissed');
      }

      setShowPermDenied(true);
    }
  };

  /** Retry permission request from the denied screen */
  const handleTryAgain = () => {
    setShowPermDenied(false);
    handleAllowAccess();
  };

  /** Back to landing from permission denied */
  const handlePermBack = () => {
    setShowPermDenied(false);
    setIsConnecting(false);
  };

  return (
    <main class={styles.page}>
      {/* Hero */}
      <div class={styles.hero}>
        <h1 class={styles.logo}>DuoCast</h1>
        <p class={styles.tagline}>Talk to someone new. Anonymously.</p>

        {/* Trust signals */}
        <div class={styles.trustSignals} role="list" aria-label="Trust signals">
          <span class={styles.trustItem} role="listitem">No sign-up needed</span>
          <span class={styles.trustDot} aria-hidden="true" />
          <span class={styles.trustItem} role="listitem">Your face is hidden until you choose</span>
          <span class={styles.trustDot} aria-hidden="true" />
          <span class={styles.trustItem} role="listitem">End anytime</span>
        </div>
      </div>

      {/* CTA Section */}
      <div class={styles.ctaSection}>
        <Show
          when={isLive()}
          fallback={
            /* Off-peak: not enough users online */
            <div class={styles.offPeakSection}>
              <p class={styles.offPeakText}>
                Next live session starts at{' '}
                <span class={styles.offPeakTime}>8 PM</span>
              </p>
              <button class="btn btn-secondary btn-lg" type="button">
                Notify Me
              </button>
            </div>
          }
        >
          {/* Start Chatting button */}
          <button
            class={`btn btn-primary btn-lg ${styles.startButton} ${
              isConnecting() ? styles.startButtonLoading : styles.startButtonPulse
            }`}
            onClick={handleStartClick}
            disabled={isConnecting()}
            type="button"
          >
            <Show when={isConnecting()}>
              <span class={styles.buttonSpinner} aria-hidden="true" />
            </Show>
            {isConnecting() ? 'Connecting...' : 'Start Chatting'}
          </button>
        </Show>

        {/* Online counter */}
        <div class={styles.onlineBadge} role="status" aria-live="polite">
          <span class={styles.onlineDot} aria-hidden="true" />
          <span>{props.onlineCount()} online now</span>
        </div>

        {/* Preferences link */}
        <button
          class={styles.prefsLink}
          onClick={() => setShowPrefs(true)}
          type="button"
        >
          Preferences
        </button>
      </div>

      {/* Footer */}
      <footer class={styles.footer}>
        <a
          href="/terms"
          class={styles.footerLink}
        >
          Terms of Service
        </a>
        <span class={styles.footerDot} aria-hidden="true" />
        <a
          href="/privacy"
          class={styles.footerLink}
        >
          Privacy Policy
        </a>
      </footer>

      {/* ===== Overlays ===== */}

      {/* Pre-permission nudge */}
      <Show when={showNudge()}>
        <PrePermissionNudge
          onAllow={handleAllowAccess}
          onLearnMore={() => {
            /* TODO: navigate to privacy explainer */
          }}
        />
      </Show>

      {/* Permission denied */}
      <Show when={showPermDenied()}>
        <PermissionDenied
          reason={permDeniedReason()}
          onTryAgain={handleTryAgain}
          onBack={handlePermBack}
        />
      </Show>

      {/* Preferences bottom sheet */}
      <BottomSheet
        isOpen={showPrefs()}
        onClose={() => setShowPrefs(false)}
        title="Preferences"
      >
        <div class={styles.prefsForm}>
          <div class={styles.prefsFieldGroup}>
            <label class={styles.prefsLabel} for="age-range">
              Age Range
            </label>
            <select
              id="age-range"
              class={styles.prefsSelect}
              value={ageRange()}
              onChange={(e) => setAgeRange(e.currentTarget.value)}
            >
              <option value="any">Any age</option>
              <option value="18-24">18 - 24</option>
              <option value="25-34">25 - 34</option>
              <option value="35-44">35 - 44</option>
              <option value="45+">45+</option>
            </select>
          </div>

          <div class={styles.prefsFieldGroup}>
            <label class={styles.prefsLabel} for="gender-pref">
              Gender Preference
            </label>
            <select
              id="gender-pref"
              class={styles.prefsSelect}
              value={genderPref()}
              onChange={(e) => setGenderPref(e.currentTarget.value)}
            >
              <option value="any">No preference</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="nonbinary">Non-binary</option>
            </select>
            <p class={styles.prefsHint}>
              Preferences improve matching but do not guarantee results.
            </p>
          </div>

          <button
            class={`btn btn-primary ${styles.prefsSaveButton}`}
            onClick={() => setShowPrefs(false)}
            type="button"
          >
            Save Preferences
          </button>
        </div>
      </BottomSheet>
    </main>
  );
};

export default LandingPage;
